import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useAppContext } from "../State/Global/AppContext";
import { useTranslation } from "react-i18next";
import { message, notification } from "antd";
import {
    getSocketConfigs,
    parseCookiesGetSessionIdCookie,
    getSessionIdCookieLocalStorage,
} from "./socketConfigs";
import CTA from "../Constants";
import io from "socket.io-client";

function useNotificationSocket() {
    const [notificationSocket, setNotificationSocket] = useState(null);
    const { actions } = useAppContext();
    const { t } = useTranslation();
    const socketMessageKey = "socketMessages";

    const connectNotificationSocket = async (socketConfigs) => {
        const allCookies = document.cookie;
        const sessionsIdCookie =
            parseCookiesGetSessionIdCookie(allCookies) || (await getSessionIdCookieLocalStorage());
        const [initSocketConfig, url] = getSocketConfigs("notifications", sessionsIdCookie);
        let configs = JSON.parse(JSON.stringify(initSocketConfig));
        if (
            socketConfigs &&
            Array.isArray(socketConfigs.transports) &&
            socketConfigs.transports.length
        ) {
            const serverSocketConfigs = JSON.parse(JSON.stringify(socketConfigs));
            configs = { ...initSocketConfig, ...serverSocketConfigs };
            configs.transports = configs.transports.filter((i) => i !== "polling");
        }
        const socket = io.connect(url, configs);
        setNotificationSocket(socket);
        actions.setNotificationSocketStatus(CTA.SOCKET_STATUS.CONNECTED);
    };

    const onNotificationSocketError = useCallback(() => {
        actions.setNotificationSocketStatus(CTA.SOCKET_STATUS.ERROR);
    }, [actions]);

    const onNotificationSocketReconnecting = useCallback(() => {
        message.loading({
            content: t("connection.reconnectingTitle"),
            key: socketMessageKey,
            duration: 0,
        });
        actions.setNotificationSocketStatus(CTA.SOCKET_STATUS.RECONNECTING);
    }, [actions, t]);

    const onNotificationSocketReconnect = useCallback(() => {
        message.destroy(socketMessageKey);
        actions.setNotificationSocketStatus(CTA.SOCKET_STATUS.CONNECTED);
    }, [actions]);

    const onNotificationServerDiconnect = useCallback(() => {
        notification["error"]({
            message: "Error",
            description: t("connection.onSocketError"),
            duration: 0,
        });
        actions.setNotificationSocketStatus(CTA.SOCKET_STATUS.DISCONNECTED);
    }, [actions, t]);

    const onNotificationSocketDisconnect = useCallback(() => {
        message.loading({
            content: t("connection.reconnectingTitle"),
            key: socketMessageKey,
            duration: 0,
        });
        actions.setNotificationSocketStatus(CTA.SOCKET_STATUS.RECONNECTING);
    }, [actions, t]);

    function disconnectNotificationSocket() {
        if (notificationSocket) {
            notificationSocket.disconnect();
        }
    }

    useEffect(() => {
        if (notificationSocket) {
            notificationSocket.on("connect_error", onNotificationSocketError);
            notificationSocket.on("reconnecting", onNotificationSocketReconnecting);
            notificationSocket.on("reconnect", onNotificationSocketReconnect);
            notificationSocket.on("disconnect", (reason) => {
                if (reason === "io client disconnect") {
                    return;
                }
                if (reason === "io server disconnect") {
                    onNotificationServerDiconnect();
                    return;
                }
                onNotificationSocketDisconnect();
            });
        }
    }, [
        notificationSocket,
        onNotificationSocketError,
        onNotificationSocketReconnecting,
        onNotificationSocketReconnect,
        onNotificationServerDiconnect,
        onNotificationSocketDisconnect,
    ]);

    return [
        notificationSocket,
        {
            connectNotificationSocket,
            disconnectNotificationSocket,
            onNotificationSocketReconnecting,
            onNotificationSocketReconnect,
        },
    ];
}
export { useNotificationSocket };
